import type { CompetitorPostDto } from '@reelplicate/api-client/model';
import { toast } from '@reelplicate/ui';
import { useState } from 'react';

import { useMakeMyVersion } from '@/features/flows/use-make-my-version';
import { useSavePost } from '@/features/media/use-save-post';
import { getErrorMessage } from '@/lib/errors';

export type PostAction = 'save' | 'remake';

/**
 * Save a competitor post to the library, or save it and start a flow to
 * make your version - one post at a time, failures as a toast.
 */
export function usePostActions(workspaceId: string) {
  const { save } = useSavePost(workspaceId);
  const makeMyVersion = useMakeMyVersion();
  const [busy, setBusy] = useState<{ id: string; action: PostAction } | null>(null);

  const run = async (post: CompetitorPostDto, action: PostAction) => {
    setBusy({ id: post.id, action });
    try {
      const done = await save(post.url);
      if (done?.status === 'FAILED') {
        toast.error(done.error ?? 'That post couldn’t be saved. Try again.');
        return;
      }
      if (!done?.asset) return;
      if (action === 'save') toast.success('Saved to your library');
      else await makeMyVersion.start(done.asset);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  return {
    save: (post: CompetitorPostDto) => void run(post, 'save'),
    remake: (post: CompetitorPostDto) => void run(post, 'remake'),
    /** What's running on this post, if anything. */
    busyOn: (postId: string): PostAction | null =>
      busy?.id === postId ? busy.action : null,
  };
}
